import { useNavigate } from 'react-router';
import { AlertTriangle, CalendarX, CheckCircle2 } from 'lucide-react';
import {
  DASHBOARD_STATUS_LABELS,
  DASHBOARD_STATUS_STYLE,
  type DashboardOrder,
} from '../lib/dashboardData';
import { SectionHeader } from './SectionHeader';

export interface NeedsAttentionPanelProps {
  /** Active orders with no tasks planned yet — they need scheduling. */
  orders: DashboardOrder[];
  limit?: number;
}

/**
 * "Needs Attention" panel — flags active orders that have no tasks scheduled
 * yet, so an operator knows what still needs planning. Empty state shows a
 * green check.
 */
export function NeedsAttentionPanel({ orders, limit = 6 }: NeedsAttentionPanelProps) {
  const navigate = useNavigate();
  const rows = orders.slice(0, limit);

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <SectionHeader
        icon={AlertTriangle}
        iconColor="text-amber-500"
        title="Needs Attention"
        onLink={() => navigate('/orders')}
      />
      {rows.length === 0 ? (
        <div className="px-3 py-8 text-center">
          <CheckCircle2 className="mx-auto mb-1.5 h-6 w-6 text-green-400" />
          <p className="text-xs italic text-slate-400">All active orders are scheduled.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((order) => (
            <button
              key={order.id}
              type="button"
              onClick={() => navigate(`/orders/${order.id}`)}
              className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-700">{order.jobNumber}</span>
                  <span
                    className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${DASHBOARD_STATUS_STYLE[order.status]}`}
                  >
                    {DASHBOARD_STATUS_LABELS[order.status]}
                  </span>
                </div>
                <div className="truncate text-[11px] text-slate-500">{order.customer}</div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1 text-amber-600">
                <CalendarX className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="text-[10px] font-medium">No tasks</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
